import { useEffect, useMemo, useState } from 'react';
import {
  Body,
  Box,
  Button,
  Caption,
  EmptyState,
  Eyebrow,
  I,
  Icon,
  Input,
  Label,
  Notice,
  Pill,
  Row,
  Select,
  Stack,
  Toggle,
  useDelayedLoading,
} from '@leanin/ui';
import { Sortable, type SortableItem, type SortableLabels } from '@leanin/ui/client';
import { Eye } from 'lucide-react';
import { useTranslations } from 'use-intl';
import {
  INTAKE_CONSENT_STATEMENT_KEYS,
  MAX_CHOICE_OPTION_CHARS,
  MAX_CHOICE_OPTIONS,
  MAX_PROMPT_CHARS,
  MAX_QUESTIONS,
  MIN_CHOICE_OPTIONS,
  type IntakeFormKind,
  type IntakeQuestionType,
} from '@leanin/shared/intake';
import { useIntakeForm } from '@/lib/queries/groups';
import { useUpsertIntakeForm } from '@/lib/mutations/groups';
import { failureFromError, isNotFoundApiError } from '@/lib/api-error';
import { AdminFormPageSkeleton } from '../AdminFormPageSkeleton';
import {
  CUSTOM_QUESTION_TYPES,
  type BuilderDraft,
  type DraftQuestion,
  type FormErrorCode,
  type QuestionErrorCode,
  type RecommendedTemplate,
  RECOMMENDED_TEMPLATES,
  appendQuestion,
  canAddQuestion,
  changeQuestionType,
  configToDraft,
  draftToUpsertBody,
  enableWithRecommendedDefaults,
  isDirty,
  isRecommendedSelected,
  makeQuestion,
  reorderQuestions,
  surfacesLiveWhileEditing,
  toggleRecommended,
  validateDraft,
} from './intake-builder-model';
import { QuestionRow } from './QuestionRow';
import { QuestionEditor } from './QuestionEditor';
import { IntakeConsentNotice } from './IntakeConsentNotice';
import { RequestToJoinGroupModal } from '@/components/groups/RequestToJoinGroupModal';
import { type ApplicantIntakeForm } from '@/components/intake';
import styles from './join-questions.module.css';

type Props = {
  readonly groupId: string;
  readonly formKind: IntakeFormKind;
  /** Group display name — feeds the preview's "Request to join {name}" header. */
  readonly groupName: string;
  /** Reports the builder's unsaved-changes state up so the section can guard a
   *  form switch. Must be stable (memoized by the caller). */
  readonly onDirtyChange?: (dirty: boolean) => void;
};

type SortableQuestion = SortableItem & { readonly raw: DraftQuestion };

// Render an unsaved draft as the applicant would see it: the preview reuses the
// real applicant modal, so it needs the wire form shape. Blank prompts fall
// back to the placeholder and blank choice options are dropped — exactly how a
// saved form resolves them.
function draftToApplicantForm(
  questions: readonly DraftQuestion[],
  promptPlaceholder: string,
): ApplicantIntakeForm {
  return {
    formVersionId: 'preview',
    versionNumber: 0,
    questions: questions.map((q) => ({
      id: q.id,
      type: q.type,
      prompt: q.prompt.trim().length > 0 ? q.prompt : promptPlaceholder,
      required: q.required,
      options: q.options.map((o) => o.trim()).filter((o) => o.length > 0),
      ...(q.prefillSource ? { prefillSource: q.prefillSource } : {}),
    })),
    consentStatementKeys: [...INTAKE_CONSENT_STATEMENT_KEYS],
  };
}

export function IntakeFormBuilder({ groupId, formKind, groupName, onDirtyChange }: Props) {
  const t = useTranslations('Group');
  const configQuery = useIntakeForm(groupId, formKind);
  const upsert = useUpsertIntakeForm();
  const config = configQuery.data;

  const [draft, setDraft] = useState<BuilderDraft | null>(null);
  const [showErrors, setShowErrors] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  // A custom question being composed but NOT yet on the form — it joins the
  // form (and the preview) only when the leader clicks "Add to form".
  const [composer, setComposer] = useState<DraftQuestion | null>(null);

  useEffect(() => {
    if (config !== undefined && draft === null) setDraft(configToDraft(config));
  }, [config, draft]);

  const dirty = draft !== null && config !== undefined ? isDirty(draft, config) : false;

  // Unsaved work includes a composer holding a typed-but-not-added question —
  // otherwise switching forms / navigating away would discard it with no
  // prompt. (Save still keys off `dirty` alone; the composer isn't on the form.)
  const composerHasContent = composer !== null && composer.prompt.trim().length > 0;
  const unsavedWork = dirty || composerHasContent;

  useEffect(() => {
    onDirtyChange?.(unsavedWork);
  }, [unsavedWork, onDirtyChange]);

  useEffect(() => {
    if (dirty) setJustSaved(false);
  }, [dirty]);

  useEffect(() => {
    if (!justSaved) return;
    const id = setTimeout(() => setJustSaved(false), 1500);
    return () => clearTimeout(id);
  }, [justSaved]);

  // Google-Forms accordion: collapse the open in-place editor when a click lands
  // outside the "On the form" list. Switching to ANOTHER row inside the list is
  // handled by that row's own onClick (single-active editingId), so in-list
  // targets are ignored — and it listens on `click`, not `mousedown`, so
  // collapsing never reflows the list out from under the click being made (which
  // would otherwise swallow a first click on a lower row).
  useEffect(() => {
    if (editingId === null) return;
    const listClass = styles.onFormList;
    if (listClass === undefined) return;
    const onClickAway = (e: MouseEvent) => {
      const target = e.target as Node | null;
      // Ignore the very click that opened this editor: opening swaps the wording
      // <button> (the click's target) for the <Input>, so by the time this
      // document-level handler runs the target is detached — and a detached node
      // must NOT be treated as "outside the list", or the editor self-collapses
      // on open. Only a click on a live node outside the list collapses.
      if (target === null || !document.contains(target)) return;
      const list = document.getElementsByClassName(listClass)[0] ?? null;
      if (list !== null && !list.contains(target)) {
        setEditingId(null);
      }
    };
    document.addEventListener('click', onClickAway);
    return () => document.removeEventListener('click', onClickAway);
  }, [editingId]);

  const validation = useMemo(() => (draft !== null ? validateDraft(draft) : null), [draft]);

  const recommendedPrompt = (key: string): string => {
    switch (key) {
      case 'why_join':
        return t('admin.joinQuestions.recommended.why_join');
      case 'region':
        return t('admin.joinQuestions.recommended.region');
      case 'sector':
        return t('admin.joinQuestions.recommended.sector');
      case 'code_of_conduct':
        return t('admin.joinQuestions.recommended.code_of_conduct');
      default:
        return '';
    }
  };

  const errorCopy = (code: QuestionErrorCode): string => {
    switch (code) {
      case 'prompt_required':
        return t('admin.joinQuestions.errors.prompt_required');
      case 'prompt_too_long':
        return t('admin.joinQuestions.errors.prompt_too_long', { max: MAX_PROMPT_CHARS });
      case 'options_min':
        return t('admin.joinQuestions.errors.options_min', { min: MIN_CHOICE_OPTIONS });
      case 'options_max':
        return t('admin.joinQuestions.errors.options_max', { max: MAX_CHOICE_OPTIONS });
      case 'options_duplicate':
        return t('admin.joinQuestions.errors.options_duplicate');
      case 'option_too_long':
        return t('admin.joinQuestions.errors.option_too_long', { max: MAX_CHOICE_OPTION_CHARS });
      case 'option_required':
        return t('admin.joinQuestions.errors.option_required');
    }
  };

  const formErrorCopy = (code: FormErrorCode): string => {
    switch (code) {
      case 'too_many_questions':
        return t('admin.joinQuestions.errors.too_many_questions', { max: MAX_QUESTIONS });
      case 'no_questions_when_enabled':
        return t('admin.joinQuestions.errors.fixBeforeSaving');
    }
  };

  const labelFor = (q: DraftQuestion): string =>
    q.prompt.trim().length > 0 ? q.prompt : t('admin.joinQuestions.question.promptPlaceholder');

  // Recommended templates not yet on the form — the "Recommended" catalog.
  const catalogTemplates = draft
    ? RECOMMENDED_TEMPLATES.filter((tpl) => !isRecommendedSelected(draft, tpl.key))
    : [];

  // The single arrangeable on-form list: recommended + freeform, in order.
  const formItems = useMemo<SortableQuestion[]>(
    () => (draft?.questions ?? []).map((q) => ({ id: q.id, label: labelFor(q), raw: q })),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- labelFor only reads `t`
    [draft, t],
  );
  const srStrings = useMemo(
    () => ({
      onPickUp: (item: string) => t('admin.joinQuestions.reorderSr.pickUp', { item }),
      onMove: (item: string, position: number, total: number) =>
        t('admin.joinQuestions.reorderSr.move', { item, position, total }),
      onDrop: (item: string, position: number) =>
        t('admin.joinQuestions.reorderSr.drop', { item, position }),
      onCancel: (item: string) => t('admin.joinQuestions.reorderSr.cancel', { item }),
    }),
    [t],
  );

  const sortableLabels = useMemo<Partial<SortableLabels>>(
    () => ({
      dragHandle: (item) => t('admin.joinQuestions.reorderControls.dragHandle', { item }),
      reorderButton: (item) => t('admin.joinQuestions.reorderControls.reorderButton', { item }),
      list: t('admin.joinQuestions.reorderControls.list'),
      picker: (item, position, total) =>
        t('admin.joinQuestions.reorderControls.picker', { item, position, total }),
      moveUp: (item) => t('admin.joinQuestions.reorderControls.moveUp', { item }),
      moveDown: (item) => t('admin.joinQuestions.reorderControls.moveDown', { item }),
      closePicker: t('admin.joinQuestions.reorderControls.closePicker'),
      reorderText: t('admin.joinQuestions.reorderControls.reorderText'),
      doneText: t('admin.joinQuestions.reorderControls.doneText'),
    }),
    [t],
  );

  const showSkeleton = useDelayedLoading(
    !configQuery.isError && (config === undefined || draft === null),
  );

  if (configQuery.isError) {
    return isNotFoundApiError(configQuery.error) ? (
      <EmptyState
        icon={I.lock}
        title={t('admin.joinQuestions.unavailable.title')}
        body={t('admin.joinQuestions.unavailable.body')}
        bordered
      />
    ) : (
      <EmptyState
        icon={I.alert}
        title={t('admin.joinQuestions.loadError.title')}
        body={t('admin.joinQuestions.loadError.body')}
        bordered
        primary={
          <Button
            variant="secondary"
            size="md"
            onClick={() => {
              void configQuery.refetch();
            }}
          >
            {t('admin.joinQuestions.loadError.retry')}
          </Button>
        }
      />
    );
  }

  if (showSkeleton) {
    return <AdminFormPageSkeleton cards={1} rowsPerCard={6} withAction />;
  }
  if (config === undefined || draft === null) return null;

  const typeLabel = (type: IntakeQuestionType): string => t(`admin.joinQuestions.type.${type}`);

  const customTypeOptions = CUSTOM_QUESTION_TYPES.map((type) => ({
    value: type,
    label: typeLabel(type),
  }));

  const setEnabled = (enabled: boolean) =>
    setDraft(
      enabled ? enableWithRecommendedDefaults(draft, recommendedPrompt) : { ...draft, enabled },
    );

  const patchQuestion = (id: string, patch: Partial<DraftQuestion>) =>
    setDraft({
      ...draft,
      questions: draft.questions.map((q) => (q.id === id ? { ...q, ...patch } : q)),
    });

  const setQuestionType = (id: string, type: IntakeQuestionType) =>
    setDraft({
      ...draft,
      questions: draft.questions.map((q) => (q.id === id ? changeQuestionType(q, type) : q)),
    });

  const removeQuestion = (id: string) => {
    if (editingId === id) setEditingId(null);
    setDraft({ ...draft, questions: draft.questions.filter((q) => q.id !== id) });
  };

  // "Add a custom question" opens an inline composer that is NOT on the form
  // yet; it joins the form (and preview) only on "Add to form".
  const openComposer = () => {
    if (!canAddQuestion(draft)) return;
    setComposer(makeQuestion(crypto.randomUUID(), 'short_answer'));
  };
  const composerValidation =
    composer !== null ? validateDraft({ enabled: true, questions: [composer] }) : null;
  const composerValid = composerValidation?.valid === true;
  // Commit is gated on validity AND remaining capacity (an open composer holds
  // a pending slot — see `canAdd` below — so this is normally always true, but
  // it also guards against a race and never silently swallows the input).
  const composerCanCommit = composerValid && canAddQuestion(draft);
  const composerErrorMessage = ((): string | null => {
    if (composer === null || composerValidation === null) return null;
    const code = composerValidation.questionErrors[composer.id];
    // Surface only actively-bad input (e.g. duplicate options) while composing;
    // a still-blank prompt just keeps "Add to form" disabled, without nagging.
    return code !== undefined && surfacesLiveWhileEditing(code) ? errorCopy(code) : null;
  })();
  const commitComposer = () => {
    if (composer === null || !composerCanCommit) return;
    const next = appendQuestion(draft, composer);
    if (next === draft) return; // rejected at the cap — keep the composer, lose nothing
    setDraft(next);
    setComposer(null);
  };

  const toggleRecommendedRow = (template: RecommendedTemplate) => {
    const present = draft.questions.some((q) => q.templateKey === template.key);
    if (present && editingId !== null) {
      const q = draft.questions.find((x) => x.templateKey === template.key);
      if (q && editingId === q.id) setEditingId(null);
    }
    setDraft(toggleRecommended(draft, template, recommendedPrompt(template.key)));
  };

  const handleReorder = (items: readonly SortableQuestion[]) =>
    setDraft(
      reorderQuestions(
        draft,
        items.map((i) => i.raw),
      ),
    );

  const handleSave = () => {
    const result = validateDraft(draft);
    if (!result.valid) {
      setShowErrors(true);
      setSaveError(t('admin.joinQuestions.errors.fixBeforeSaving'));
      return;
    }
    setSaveError(null);
    upsert.mutate(
      { groupId, formKind, body: draftToUpsertBody(draft) },
      {
        onSuccess: (saved) => {
          setDraft(configToDraft(saved));
          setShowErrors(false);
          setEditingId(null);
          setJustSaved(true);
        },
        onError: (err) => {
          setSaveError(failureFromError(err, t('admin.joinQuestions.saveError')).message);
        },
      },
    );
  };

  const handleDiscard = () => {
    setDraft(configToDraft(config));
    setShowErrors(false);
    setSaveError(null);
    setEditingId(null);
    setComposer(null);
  };

  // An open composer occupies a pending slot, so the catalog can't fill the
  // form to the cap underneath it (which would make the composed question
  // un-addable). Once the composer commits or cancels, the slot frees up.
  const canAdd = draft.questions.length + (composer !== null ? 1 : 0) < MAX_QUESTIONS;
  // A question's error shows once a Save attempt fails (`showErrors`), and —
  // for the row currently being edited — the moment an actively-bad value is
  // entered (e.g. two identical multiple-choice options), so the leader is
  // warned before pressing Save rather than after.
  const errorVisibleFor = (id: string): 'save' | 'live' | null => {
    if (validation === null) return null;
    const code = validation.questionErrors[id];
    if (code === undefined) return null;
    if (showErrors) return 'save';
    if (editingId === id && surfacesLiveWhileEditing(code)) return 'live';
    return null;
  };
  const questionErrorFor = (id: string): string | null => {
    const code = validation?.questionErrors[id];
    return errorVisibleFor(id) !== null && code !== undefined ? errorCopy(code) : null;
  };
  const hasError = (id: string): boolean => errorVisibleFor(id) !== null;
  const isExpanded = (id: string): boolean => editingId === id || hasError(id);
  const toggleEdit = (id: string) => setEditingId((current) => (current === id ? null : id));

  return (
    <Stack gap="lg">
      <Box border="hairline" rounded="lg" bg="card" style={{ overflow: 'hidden' }}>
        <Row justify="space-between" align="center" gap="md" px="xl" py="lg">
          <Row gap="sm" align="center" style={{ minWidth: 0 }}>
            <Eyebrow>{t('admin.joinQuestions.title')}</Eyebrow>
            <Pill size="xs" tone="emerald">
              {t('admin.joinQuestions.newBadge')}
            </Pill>
          </Row>
          <Row gap="sm" align="center" style={{ flexShrink: 0 }}>
            <Label>
              {draft.enabled
                ? t('admin.joinQuestions.enable.on')
                : t('admin.joinQuestions.enable.off')}
            </Label>
            <Toggle
              on={draft.enabled}
              onChange={() => setEnabled(!draft.enabled)}
              label={t('admin.joinQuestions.enable.srLabel')}
            />
          </Row>
        </Row>

        <Stack gap="xl" px="xl" pb="xl">
          {/* The banner slot always tells the truth about the draft × saved
              matrix. The toggle reads as "applies now" but is part of the
              draft, so both unsaved directions get their own warning:
                off-draft, off-saved, has questions → danger: the form is off.
                off-draft, ON-saved            → warning: still live until saved.
                on-draft,  off-saved           → warning: not live until saved.
                on-draft,  on-saved            → nothing.
              Only an empty never-enabled builder (nothing to warn about)
              stays quiet. */}
          {!draft.enabled && !config.enabled && draft.questions.length > 0 && (
            <Notice intent="danger" title={t('admin.joinQuestions.enable.offNote')} />
          )}
          {!draft.enabled && config.enabled && (
            <Notice intent="warning" title={t('admin.joinQuestions.enable.unsavedOffNote')} />
          )}
          {draft.enabled && !config.enabled && (
            <Notice intent="warning" title={t('admin.joinQuestions.enable.unsavedOnNote')} />
          )}

          {/* RECOMMENDED — the recommended catalog (templates not yet on the
              form). Selecting one adds it to the arrangeable list below. */}
          <Stack gap="sm">
            <Row gap="xs" align="center">
              <Eyebrow>{t('admin.joinQuestions.sections.add')}</Eyebrow>
              <Icon size="sm" color="var(--muted-foreground)">
                {I.spark}
              </Icon>
            </Row>

            {catalogTemplates.length > 0 && (
              <Stack gap="none">
                {catalogTemplates.map((template, idx) => {
                  const prompt = recommendedPrompt(template.key);
                  return (
                    <QuestionRow
                      key={template.key}
                      handlePlaceholder
                      divider={idx > 0}
                      padX="none"
                      padY="md"
                      muted
                      checked={false}
                      checkboxDisabled={!canAdd}
                      onToggleChecked={() => toggleRecommendedRow(template)}
                      checkboxLabel={t('admin.joinQuestions.recommended.checkboxLabel', { prompt })}
                      prompt={prompt}
                      helper={<Caption>{t('admin.joinQuestions.recommended.tapToAdd')}</Caption>}
                      typeLabel={typeLabel(template.type)}
                    />
                  );
                })}
              </Stack>
            )}
          </Stack>

          {/* FREEFORM — compose a custom question, then add it to the form. */}
          <Stack gap="sm">
            <Eyebrow>{t('admin.joinQuestions.sections.freeform')}</Eyebrow>

            {composer !== null ? (
              <Box
                rounded="lg"
                p="md"
                role="group"
                aria-label={t('admin.joinQuestions.addToForm')}
                style={{ background: 'var(--light-poppy)' }}
              >
                <Stack gap="sm">
                  <Row gap="sm" align="flex-start">
                    <Input
                      autoFocus
                      value={composer.prompt}
                      maxLength={MAX_PROMPT_CHARS}
                      placeholder={t('admin.joinQuestions.question.promptPlaceholder')}
                      aria-label={t('admin.joinQuestions.question.promptPlaceholder')}
                      invalid={composerErrorMessage != null}
                      onChange={(e) => setComposer({ ...composer, prompt: e.target.value })}
                      style={{ flex: 1, minWidth: 0 }}
                    />
                    <div style={{ width: 152, flexShrink: 0 }}>
                      <Select
                        value={composer.type}
                        aria-label={t('admin.joinQuestions.question.typeLabel')}
                        options={customTypeOptions.map((o) => ({ value: o.value, label: o.label }))}
                        onChange={(e) =>
                          setComposer(
                            changeQuestionType(composer, e.target.value as IntakeQuestionType),
                          )
                        }
                        style={{ width: '100%' }}
                      />
                    </div>
                  </Row>
                  {composer.prompt.length > MAX_PROMPT_CHARS - 40 && (
                    <Body size="sm" color="mute">
                      {composer.prompt.length} / {MAX_PROMPT_CHARS}
                    </Body>
                  )}
                  {composerErrorMessage != null && (
                    <span role="alert" aria-live="polite">
                      <Body size="sm" color="destructive">
                        {composerErrorMessage}
                      </Body>
                    </span>
                  )}
                  <QuestionEditor
                    question={composer}
                    onPatch={(patch) => setComposer({ ...composer, ...patch })}
                    footer={
                      <Row gap="sm" align="center">
                        <Button
                          variant="primary"
                          size="sm"
                          icon={I.plus}
                          disabled={!composerCanCommit}
                          onClick={commitComposer}
                        >
                          {t('admin.joinQuestions.addToForm')}
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setComposer(null)}>
                          {t('admin.joinQuestions.composerCancel')}
                        </Button>
                      </Row>
                    }
                  />
                </Stack>
              </Box>
            ) : (
              <Row gap="md" align="center">
                <Button
                  variant="link"
                  size="md"
                  icon={I.plus}
                  onClick={openComposer}
                  disabled={!canAdd}
                >
                  {t('admin.joinQuestions.addQuestion')}
                </Button>
                {!canAdd && (
                  <Caption>{t('admin.joinQuestions.limitReached', { max: MAX_QUESTIONS })}</Caption>
                )}
              </Row>
            )}
          </Stack>

          {/* ON THE FORM PREVIEW — one arrangeable list of every added question
              (recommended + freeform, drag into any order), then the always-shown
              consent statements. Preview + save follow this order. Recessed into
              its own panel: the sections above are the palette of things to add,
              this is the canvas — the form itself — and the two shouldn't read
              as one continuous list. */}
          <Stack gap="sm">
            <Eyebrow>{t('admin.joinQuestions.sections.onForm')}</Eyebrow>
            <Box border="hairline" rounded="lg" p="lg" bg="background">
              <Stack gap="md">
                {draft.questions.length > 0 ? (
                  <Sortable<SortableQuestion>
                    items={formItems}
                    onReorder={handleReorder}
                    srStrings={srStrings}
                    labels={sortableLabels}
                    className={styles.onFormList}
                  >
                    {(item, dragHandle) => {
                      const q = item.raw;
                      const prompt =
                        q.prompt.trim().length > 0
                          ? q.prompt
                          : t('admin.joinQuestions.question.promptPlaceholder');
                      return (
                        <QuestionRow
                          rounded
                          padX="md"
                          padY="sm"
                          dragHandle={dragHandle}
                          checked
                          onToggleChecked={(checked) => {
                            if (!checked) removeQuestion(q.id);
                          }}
                          checkboxLabel={t('admin.joinQuestions.question.includeLabel', { prompt })}
                          prompt={prompt}
                          typeLabel={typeLabel(q.type)}
                          question={q}
                          editing={isExpanded(q.id)}
                          onEdit={() => toggleEdit(q.id)}
                          editLabel={t('admin.joinQuestions.question.editLabel', { prompt })}
                          collapseLabel={t('admin.joinQuestions.question.collapseLabel', {
                            prompt,
                          })}
                          doneLabel={t('admin.joinQuestions.question.done')}
                          promptPlaceholder={t('admin.joinQuestions.question.promptPlaceholder')}
                          typeSelectLabel={t('admin.joinQuestions.question.typeLabel')}
                          allowTypeChange={q.type !== 'declaration'}
                          typeOptions={customTypeOptions}
                          onPatch={(patch) => patchQuestion(q.id, patch)}
                          onChangeType={(type) => setQuestionType(q.id, type)}
                          errorMessage={questionErrorFor(q.id)}
                          onRemove={() => removeQuestion(q.id)}
                          removeLabel={t('admin.joinQuestions.question.remove')}
                        />
                      );
                    }}
                  </Sortable>
                ) : (
                  <Caption>{t('admin.joinQuestions.emptyForm')}</Caption>
                )}

                <IntakeConsentNotice />
              </Stack>
            </Box>
          </Stack>
        </Stack>

        <Box px="xl" py="md" style={{ borderTop: '1px solid var(--border)' }}>
          <Row justify="space-between" align="center" gap="md" wrap>
            <div aria-live="polite" style={{ minWidth: 0 }}>
              {saveError !== null ? (
                <Body size="sm" color="destructive">
                  {saveError}
                </Body>
              ) : justSaved ? (
                <Body size="sm" color="success">
                  {t('admin.joinQuestions.footer.saved')}
                </Body>
              ) : (
                <Body size="sm" color="mute">
                  {t('admin.joinQuestions.footer.count', {
                    count: draft.questions.length,
                    max: MAX_QUESTIONS,
                  })}
                </Body>
              )}
              {showErrors && validation?.formError != null && saveError === null && (
                <Body size="sm" color="destructive">
                  {formErrorCopy(validation.formError)}
                </Body>
              )}
            </div>
            <Row gap="sm" align="center">
              {dirty && (
                <Button
                  variant="ghost"
                  size="md"
                  onClick={handleDiscard}
                  disabled={upsert.isPending}
                >
                  {t('admin.joinQuestions.footer.discard')}
                </Button>
              )}
              <Button
                variant="secondary"
                size="md"
                icon={<Eye size={16} strokeWidth={1.75} aria-hidden />}
                onClick={() => setPreviewOpen(true)}
              >
                {t('admin.joinQuestions.footer.previewForm')}
              </Button>
              <Button
                variant="primary"
                size="md"
                icon={I.check}
                onClick={handleSave}
                disabled={!dirty}
                loading={upsert.isPending}
              >
                {t('admin.joinQuestions.footer.save')}
              </Button>
            </Row>
          </Row>
        </Box>
      </Box>

      {/* Preview reuses the real applicant modal in preview mode — same shell,
          header, fields, separators, consent, and footer as the live
          Request-to-join form. Mounted only while open so its data hooks stay
          idle in the background; it renders the current draft and never submits. */}
      {previewOpen && (
        <RequestToJoinGroupModal
          open
          onClose={() => setPreviewOpen(false)}
          isPreview
          previewForm={draftToApplicantForm(
            draft?.questions ?? [],
            t('admin.joinQuestions.question.promptPlaceholder'),
          )}
          groupId={groupId}
          groupKind={formKind === 'member_to_circle' ? 'circle' : 'network'}
          groupName={groupName}
          onSubmit={async () => ({ ok: true })}
        />
      )}
    </Stack>
  );
}
